/**
 * Fetch-based Database for Admin Panel
 * Connects to self-hosted Node.js JSON API
 */

const DB_VERSION = '1.3';

const DEFAULT_NAVBAR = [
  { id: 'nav-1', title: 'Protocol', slug: 'protocol', type: 'section', url: '#projects', sortOrder: 1, isDefault: true, isActive: true, target: '_self' },
  { id: 'nav-2', title: 'Tech', slug: 'tech', type: 'section', url: '#skills', sortOrder: 2, isDefault: true, isActive: true, target: '_self' },
  { id: 'nav-3', title: 'Milestones', slug: 'milestones', type: 'section', url: '#experience', sortOrder: 3, isDefault: true, isActive: true, target: '_self' },
  { id: 'nav-4', title: 'Vision', slug: 'vision', type: 'section', url: '#about', sortOrder: 4, isDefault: true, isActive: true, target: '_self' },
  { id: 'nav-5', title: 'Infrastructure', slug: 'infrastructure', type: 'section', url: '#infrastructure', sortOrder: 5, isDefault: true, isActive: true, target: '_self' },
  { id: 'nav-6', title: 'Funding', slug: 'funding', type: 'section', url: '#funding', sortOrder: 6, isDefault: true, isActive: true, target: '_self' }
];

const DEFAULT_PAGES = {};

const DEFAULT_CARDS = [
  {
    id: 'card-1',
    tag: 'Consensus Layer',
    title: 'Phase 1: Quantum Foundations',
    description: 'Establishing the mathematical framework for quantum-resistant consensus protocols using lattice-based cryptography and zero-knowledge proofs.',
    image: '',
    buttonText: 'Explore →',
    buttonLink: '#',
    sortOrder: 1,
    isActive: true
  },
  {
    id: 'card-2',
    tag: 'Testnet',
    title: 'Phase 2: Lattice Testnet',
    description: 'Deploying a public testnet to validate quantum-safe transaction processing, with 10,000+ nodes participating in consensus simulations.',
    image: '',
    buttonText: 'Join Testnet →',
    buttonLink: '#',
    sortOrder: 2,
    isActive: true
  },
  {
    id: 'card-3',
    tag: 'Smart Contracts',
    title: 'Phase 3: Q-Contracts',
    description: 'Developing quantum-resistant smart contract language with formal verification, enabling complex DeFi protocols immune to quantum attacks.',
    image: '',
    buttonText: 'Documentation →',
    buttonLink: '#',
    sortOrder: 3,
    isActive: true
  },
  {
    id: 'card-4',
    tag: 'Mainnet',
    title: 'Phase 4: Daily Mainnet',
    description: 'Launching the production quantum-secure blockchain with sub-second finality, 100,000+ TPS, and full EVM compatibility layer.',
    image: '',
    buttonText: 'Launch App →',
    buttonLink: '#',
    sortOrder: 4,
    isActive: true
  }
];

const DEFAULT_MILESTONES = {
  sectionLabel: 'Milestones',
  heading: 'Building the quantum future',
  subheading: '',
  backgroundImage: '',
  items: [
    {
      id: 'milestone-1',
      period: '2026 — Present',
      title: 'Mainnet Launch',
      subtitle: 'Daily Network Live',
      description: 'The world\'s first quantum-resistant blockchain goes live with full EVM compatibility, enabling secure DeFi, NFTs, and enterprise applications.',
      sortOrder: 1,
      isActive: true
    },
    {
      id: 'milestone-2',
      period: '2025 — Breakthrough',
      title: 'Q-Contracts Testnet',
      subtitle: '10,000+ Nodes',
      description: 'Successfully deployed the quantum-safe smart contract platform with formal verification, attracting major DeFi protocols to test migration paths.',
      sortOrder: 2,
      isActive: true
    },
    {
      id: 'milestone-3',
      period: '2024 — Growth',
      title: 'Lattice Testnet v2',
      subtitle: 'Consensus Validated',
      description: 'Achieved sub-second finality across 50,000 validator nodes using our novel lattice-based consensus mechanism, proving scalability at scale.',
      sortOrder: 3,
      isActive: true
    },
    {
      id: 'milestone-4',
      period: '2023 — Foundation',
      title: 'Vision & Seed Round',
      subtitle: 'Daily Network Founded',
      description: 'Assembled a world-class team of quantum physicists, blockchain engineers, and cryptographers. Raised seed funding to pioneer the intersection of quantum computing and distributed ledgers.',
      sortOrder: 4,
      isActive: true
    }
  ]
};

// In-memory cache to support synchronous reads
let dataCache = {
  daily_navbar_items: [...DEFAULT_NAVBAR],
  daily_pages: { ...DEFAULT_PAGES },
  daily_protocol_cards: [...DEFAULT_CARDS],
  daily_milestones: JSON.parse(JSON.stringify(DEFAULT_MILESTONES)),
  daily_infrastructure: [],
  daily_funding_requests: []
};

// API Endpoint (Nginx reverse proxies /api to port 3000)
const API_URL = '/api/data';

export class Storage {
  static async init() {
    try {
      const res = await fetch(`${API_URL}?t=${Date.now()}`, { cache: 'no-store' });
      if (res.ok) {
        const serverData = await res.json();
        // Merge server data with defaults
        dataCache.daily_navbar_items = serverData.daily_navbar_items || [...DEFAULT_NAVBAR];
        dataCache.daily_pages = serverData.daily_pages || { ...DEFAULT_PAGES };
        dataCache.daily_protocol_cards = serverData.daily_protocol_cards || [...DEFAULT_CARDS];
        dataCache.daily_milestones = serverData.daily_milestones || JSON.parse(JSON.stringify(DEFAULT_MILESTONES));
        dataCache.daily_infrastructure = serverData.daily_infrastructure || [];
        dataCache.daily_funding_requests = serverData.daily_funding_requests || [];
      } else {
        // If server hasn't saved yet, perform initial save of defaults
        await this.syncToServer();
      }
    } catch (e) {
      console.warn('API not reachable, falling back to initial data. Ensure the Node.js backend is running.');
    }
  }

  static async syncToServer(key, value) {
    if (!key) return;
    try {
      await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, value })
      });
    } catch (e) {
      console.error('Failed to sync data to server:', e);
    }
  }

  // --- Navbar ---
  static getNavbarItems() {
    return dataCache.daily_navbar_items || [];
  }

  static saveNavbarItems(items) {
    dataCache.daily_navbar_items = items;
    this.syncToServer('daily_navbar_items', items);
  }

  static addNavbarItem(item) {
    const items = this.getNavbarItems();
    const newItem = {
      id: `nav-${Date.now()}`,
      ...item,
      sortOrder: items.length + 1,
      isDefault: false,
      isActive: true
    };
    items.push(newItem);
    this.saveNavbarItems(items);
    return newItem;
  }

  static updateNavbarItem(id, updates) {
    const items = this.getNavbarItems().map(item =>
      item.id === id ? { ...item, ...updates } : item
    );
    this.saveNavbarItems(items);
  }

  static deleteNavbarItem(id) {
    const items = this.getNavbarItems().filter(item => item.id !== id);
    this.saveNavbarItems(items);
  }

  static reorderNavbarItems(itemIds) {
    const items = this.getNavbarItems();
    const reordered = itemIds.map(id => items.find(item => item.id === id)).filter(Boolean);
    reordered.forEach((item, index) => { item.sortOrder = index + 1; });
    this.saveNavbarItems(reordered);
  }

  // --- Cards ---
  static getCards() {
    return dataCache.daily_protocol_cards || [];
  }

  static saveCards(cards) {
    dataCache.daily_protocol_cards = cards;
    this.syncToServer('daily_protocol_cards', cards);
  }

  static addCard(card) {
    const cards = this.getCards();
    const newCard = {
      id: `card-${Date.now()}`,
      ...card,
      sortOrder: cards.length + 1,
      isActive: true
    };
    cards.push(newCard);
    this.saveCards(cards);
    return newCard;
  }

  static updateCard(id, updates) {
    const cards = this.getCards().map(c =>
      c.id === id ? { ...c, ...updates } : c
    );
    this.saveCards(cards);
  }

  static deleteCard(id) {
    const cards = this.getCards().filter(c => c.id !== id);
    this.saveCards(cards);
  }

  static reorderCards(cardIds) {
    const cards = this.getCards();
    const reordered = cardIds.map(id => cards.find(c => c.id === id)).filter(Boolean);
    reordered.forEach((c, index) => { c.sortOrder = index + 1; });
    this.saveCards(reordered);
  }

  // --- Pages ---
  static getAllPages() {
    return dataCache.daily_pages || {};
  }

  static saveAllPages(pages) {
    dataCache.daily_pages = pages;
    this.syncToServer('daily_pages', pages);
  }

  static getPage(slug) {
    return this.getAllPages()[slug];
  }

  static savePage(slug, data) {
    const pages = this.getAllPages();
    pages[slug] = data;
    this.saveAllPages(pages);
  }

  static deletePage(slug) {
    const pages = this.getAllPages();
    delete pages[slug];
    this.saveAllPages(pages);
  }

  // --- Milestones ---
  static getMilestones() {
    return dataCache.daily_milestones || { items: [] };
  }

  static saveMilestones(data) {
    dataCache.daily_milestones = data;
    this.syncToServer('daily_milestones', data);
  }

  static addMilestoneItem(item) {
    const data = this.getMilestones();
    const newItem = {
      id: `milestone-${Date.now()}`,
      ...item,
      sortOrder: data.items.length + 1,
      isActive: true
    };
    data.items.push(newItem);
    this.saveMilestones(data);
    return newItem;
  }

  static updateMilestoneItem(id, updates) {
    const data = this.getMilestones();
    data.items = data.items.map(item =>
      item.id === id ? { ...item, ...updates } : item
    );
    this.saveMilestones(data);
  }

  static deleteMilestoneItem(id) {
    const data = this.getMilestones();
    data.items = data.items.filter(item => item.id !== id);
    this.saveMilestones(data);
  }

  static reorderMilestoneItems(itemIds) {
    const data = this.getMilestones();
    const reordered = itemIds.map(id => data.items.find(i => i.id === id)).filter(Boolean);
    reordered.forEach((item, index) => { item.sortOrder = index + 1; });
    data.items = reordered;
    this.saveMilestones(data);
  }

  // --- Infrastructure ---
  static getInfrastructure() {
    return dataCache.daily_infrastructure || [];
  }

  static saveInfrastructure(items) {
    dataCache.daily_infrastructure = items;
    this.syncToServer('daily_infrastructure', items);
  }

  static hasActiveInfrastructure() {
    return this.getInfrastructure().some(item => item.isActive);
  }

  static getActiveInfrastructure() {
    return this.getInfrastructure().filter(i => i.isActive).sort((a, b) => a.sortOrder - b.sortOrder);
  }

  static addInfrastructureItem(item) {
    const items = this.getInfrastructure();
    const newItem = {
      id: `infra-${Date.now()}`,
      ...item,
      sortOrder: items.length + 1,
      isActive: true
    };
    items.push(newItem);
    this.saveInfrastructure(items);
    return newItem;
  }

  static updateInfrastructureItem(id, updates) {
    const items = this.getInfrastructure().map(i =>
      i.id === id ? { ...i, ...updates } : i
    );
    this.saveInfrastructure(items);
  }

  static deleteInfrastructureItem(id) {
    const items = this.getInfrastructure().filter(i => i.id !== id);
    this.saveInfrastructure(items);
  }

  static reorderInfrastructureItems(itemIds) {
    const items = this.getInfrastructure();
    const reordered = itemIds.map(id => items.find(i => i.id === id)).filter(Boolean);
    reordered.forEach((i, index) => { i.sortOrder = index + 1; });
    this.saveInfrastructure(reordered);
  }

  // --- Funding Requests ---
  static getFundingRequests() {
    return dataCache.daily_funding_requests || [];
  }

  static saveFundingRequests(items) {
    dataCache.daily_funding_requests = items;
    this.syncToServer('daily_funding_requests', items);
  }

  static getFundingRequest(id) {
    return this.getFundingRequests().find(req => req.id === id);
  }

  static addFundingRequest(request) {
    const items = this.getFundingRequests();
    const newRequest = {
      id: `fund-${Date.now()}`,
      date: new Date().toISOString(),
      status: 'pending',
      ...request
    };
    items.push(newRequest);
    this.saveFundingRequests(items);
    return newRequest;
  }

  static updateFundingRequest(id, updates) {
    const items = this.getFundingRequests().map(req =>
      req.id === id ? { ...req, ...updates } : req
    );
    this.saveFundingRequests(items);
  }

  static deleteFundingRequest(id) {
    const items = this.getFundingRequests().filter(req => req.id !== id);
    this.saveFundingRequests(items);
  }

  // --- Utility ---
  static clearAll() {
    dataCache = {
      daily_navbar_items: [...DEFAULT_NAVBAR],
      daily_pages: { ...DEFAULT_PAGES },
      daily_protocol_cards: [...DEFAULT_CARDS],
      daily_milestones: JSON.parse(JSON.stringify(DEFAULT_MILESTONES)),
      daily_infrastructure: [],
      daily_funding_requests: []
    };
    // Sync each key individually since backend now expects single keys
    this.syncToServer('daily_navbar_items', dataCache.daily_navbar_items);
    this.syncToServer('daily_pages', dataCache.daily_pages);
    this.syncToServer('daily_protocol_cards', dataCache.daily_protocol_cards);
    this.syncToServer('daily_milestones', dataCache.daily_milestones);
    this.syncToServer('daily_infrastructure', dataCache.daily_infrastructure);
    this.syncToServer('daily_funding_requests', dataCache.daily_funding_requests);
    this.syncToServer('daily_contact_info', dataCache.daily_contact_info);
  }
}
